import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ChevronRight, Play } from 'lucide-react';
import { ASSOC, IMAGES } from '../mock';
import { useAuth } from '../context/AuthContext';
import EditableImage from '../components/EditableImage';
import EditableText from '../components/EditableText';

function SectionTitle({ children }) {
  return (
    <h2 className="font-title text-2xl md:text-3xl mb-4" style={{ color: 'var(--maroon)' }}>
      {children}
    </h2>
  );
}

export default function Home() {
  const { settings } = useAuth();
  const header = (settings && settings.header) || {};
  const layout = (settings && settings.layout) || {};
  const title = header.title || ASSOC.name;
  const motto = header.motto || ASSOC.motto;
  return (
    <div className="fade-in">
      <div className="max-w-6xl mx-auto px-4 py-10">
        <p className="font-title text-center text-xl md:text-2xl mb-4" style={{ color: 'var(--navy)' }}>
          Welcome to the {title} Website.
        </p>
        <div className="flex justify-center mb-10">
          <Link to="/promo" data-testid="home-watch-promo" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-title text-sm md:text-base shadow-md transition-transform hover:-translate-y-0.5" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full" style={{ background: 'var(--cream)', color: 'var(--maroon)' }}><Play size={14} fill="currentColor" /></span>
            Watch: 30 seconds
          </Link>
        </div>

        <div className="grid md:grid-cols-12 gap-8 items-start">
          {/* Left: membership PDF */}
          <div className="md:col-span-3 flex flex-col items-center">
            <Link
              to="/members/apply"
              className="flex flex-col items-center group transition-transform hover:-translate-y-1"
            >
              <div
                className="flex items-center justify-center w-24 h-28 rounded shadow-md"
                style={{ background: 'white', border: '1px solid rgba(0,0,0,0.1)' }}
              >
                <FileText size={48} style={{ color: 'var(--maroon)' }} />
              </div>
              <span className="mt-2 text-sm text-center" style={{ color: 'var(--ink)' }}>
                Apply for Membership
              </span>
            </Link>
          </div>

          {/* Middle: text */}
          <div className="md:col-span-5">
            <SectionTitle>{motto}</SectionTitle>
            <div className="space-y-4 text-[15px] leading-relaxed">
              <EditableText as="p" k="home.p0" className="block" text="Tasmania has a continuous military engineering heritage dating from the first European settlement until 12 Field Squadron RAE was disbanded in December 1987." />
              <EditableText as="p" k="home.p1" className="block" text="The RAE Association of Tasmania’s primary mission is to support all past, present and future Sappers associated with, or interested in, Tasmania." />
              <EditableText as="p" k="home.p2" className="block" text="We also see it as an important function of our Association to continue to preserve this State’s military engineering heritage and traditions." />
              <EditableText as="p" k="home.p3" className="block" text="This website provides a repository for members and a single contact point to preserve and administer this heritage." />
            </div>
          </div>

          {/* Right: photo */}
          <div className="md:col-span-4">
            <div className="rounded shadow-md overflow-hidden" style={{ border: '4px solid white' }}>
              <EditableImage imgKey="home-group" src={IMAGES.groupPhoto} alt="Tasmanian Sappers" className="w-full h-56 object-cover" />
            </div>
            <p className="text-xs italic mt-2 text-center" style={{ color: 'var(--olive)' }}>
              12 Field Squadron RAE — on parade
            </p>
          </div>
        </div>

        {/* Quick links */}
        {layout.showHomeCards !== false && (
        <div className="grid sm:grid-cols-3 gap-5 mt-12">
          {[
            { t: 'Our History', d: 'Explore 12 Field Squadron and Tasmania\u2019s engineering heritage.', to: '/history/12-field-squadron' },
            { t: 'Traditions', d: 'Ubique \u2014 the customs and Corps spirit we keep alive.', to: '/traditions' },
            { t: 'Memorials', d: 'Remembering the Sappers of Tasmania. Lest We Forget.', to: '/history/memorials' },
          ].map((c) => (
            <Link
              key={c.t}
              to={c.to}
              className="block bg-white rounded-lg p-5 shadow-sm border transition-all hover:shadow-md hover:-translate-y-1"
              style={{ borderColor: 'rgba(0,0,0,0.08)' }}
            >
              <h3 className="font-title text-lg mb-1" style={{ color: 'var(--navy)' }}>
                {c.t}
              </h3>
              <p className="text-sm mb-2">{c.d}</p>
              <span className="inline-flex items-center text-sm font-title" style={{ color: 'var(--maroon)' }}>
                Read more <ChevronRight size={15} />
              </span>
            </Link>
          ))}
        </div>
        )}
      </div>
    </div>
  );
}
